import React from "react";
import { NavLink } from "react-router-dom";
import { IResponsive, BreakPoints } from "src/types/responsiveness";
import {
  StyledTypographyHeaderItem,
  StyledTypographyHeaderSubItem,
  StyledTypographyHeaderSubItemDesc,
} from "src/components/Styled/typography";
import { useIsMobile } from "src/hooks";
import { DownOutlined } from "@ant-design/icons";
import classnames from "classnames";
import type { MenuProps } from "antd/es/menu";
import styled, { css, createGlobalStyle } from "styled-components";
import { Dropdown, Space } from "antd";
import {
  stratosGreenColor,
  stratosBackgroundColor,
} from "src/constants/colors";

export interface CustomMenuItem {
  id: number;
  name: string;
  icon?: string;
  showIcon?: boolean;
  showDivider?: boolean;
  description?: string;
  route?: string;
}

interface IProps {
  menuItemsData: CustomMenuItem[];
  itemOverlayClassName: string;
}

interface IMenuListItem {
  menuItem: CustomMenuItem;
}

type MenuItem = Required<MenuProps>["items"][number];

const MyDropdown = styled(Dropdown)`
  /* display: flex; */
`;

const elementPlacement = "bottom";

interface IGlobal extends IResponsive {
  whiteColor?: boolean;
  overlayClassName: string;
}

const GlobalStyle = createGlobalStyle<IGlobal>`
  body {
    .ant-dropdown {

      &.${({ overlayClassName }) => overlayClassName} {
        position: absolute;
        right: 0;
        pointer-events:auto;

        &.ant-dropdown-placement-${elementPlacement} {

          ul {
            background-color: rgba(15, 6, 34 );
            background-color:${stratosBackgroundColor};
            border-radius: 10px;
            border: 1px rgba(127, 102, 187, 0.1) solid;

            padding: 0;

            margin-bottom: 0px;
            margin-top: -15px;

            .menu_nav_link {
              width: 100%;
            }

            gap: 5px;
            li {
              &.ant-dropdown-menu-item-active {
                // nice shadow over hover
                background: rgba(255, 255, 255, 0.1);

              }

              // that code is for 2 column layout
              // &:nth-child(1) {
              //   width: 598px;
              //   background-color: transparent;
              // }
              // &:nth-child(n+2) {
              //   max-width:300px;
              // }
              // &:nth-child(n+5) {
              //   margin-left: -300px;
              // }
              // &:nth-child(5) {
              //   margin-top: 92px;
              // }
              // end

              border-radius: 10px;
            }
          }
        }

        &.ant-dropdown-hidden {
          // display: block;
        }
      }
    }
  }
`;

const SelectValidator = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  .ant-space {
    &:hover {
      color: ${stratosGreenColor};
      cursor: pointer;

      .name_details {
        span.item_header {
          color: ${stratosGreenColor};
        }
      }
    }
  }
`;

const ValidatorItemContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding-top: 5px;
  gap: 5px;
  &.with-border {
    padding-bottom: 10px;
    cursor: auto;
    padding-top: 10px;
    border-bottom: 1px rgba(127, 102, 187, 0.1) solid;
  }
`;

const ValidatorNameInfo = styled.div<{ $fontFamily?: string }>`
  display: flex;
  flex-direction: row;
  img.avatar {
    height: "32px";
    margin-right: 1rem;
    align-self: center;
  }
`;

const ValidatoNameDetails = styled.div<IResponsive>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  .name_details {
    height: "42px";
    flex-direction: row;
    justify-content: space-between;
    display: flex;
    align-items: center;

    span.validator_name {
      color: #fff;
      &.item_header {
        ${({ $viewName }) => {
          switch ($viewName) {
            case BreakPoints.isMd:
              return css`
                font-size: 1rem;
                line-height: 1.5rem;
              `;
            default:
              return css`
                font-size: 16px;
                line-height: 24px;
              `;
          }
        }}
      }
    }

    img {
      height: "20px";
    }
  }
`;

const ValidatoDescriptionDetails = styled.div`
  display: flex;

  span.item_description {
    color: #fff;
  }
`;

function getItem(
  label: React.ReactNode,
  key?: React.Key | null,
  icon?: React.ReactNode,
  children?: MenuItem[],
): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}

function getMenuItems(menuItems: CustomMenuItem[]): MenuItem[] {
  return (
    menuItems &&
    menuItems.map((item) => {
      const { id } = item;

      return getItem("", id, <MenuListItem menuItem={item} />);
    })
  );
}

const MenuListItem: React.FC<IMenuListItem> = ({ menuItem }) => {
  const { name, icon, showDivider, description, id, route } = menuItem;

  const { viewName } = useIsMobile();

  return (
    <NavLink className="menu_nav_link" to={route || "/"}>
      {() => (
        <ValidatorItemContainer
          className={showDivider ? "with-border" : "regular"}
        >
          <ValidatorNameInfo>
            {!!icon && <img src={icon} className="avatar" alt="" />}
            <ValidatoNameDetails $viewName={viewName}>
              <div className="name_details">
                {id === 1 && (
                  <StyledTypographyHeaderItem
                    className={classnames(["validator_name", "item_header"])}
                  >
                    {name}
                  </StyledTypographyHeaderItem>
                )}
                {id !== 1 && (
                  <StyledTypographyHeaderSubItem
                    className={classnames(["validator_name"])}
                  >
                    {name}
                  </StyledTypographyHeaderSubItem>
                )}
              </div>
            </ValidatoNameDetails>
          </ValidatorNameInfo>
          <ValidatoDescriptionDetails className="menu_details">
            <StyledTypographyHeaderSubItemDesc className="item_description">
              {description}
            </StyledTypographyHeaderSubItemDesc>
          </ValidatoDescriptionDetails>
        </ValidatorItemContainer>
      )}
    </NavLink>
  );
};

const UseCasesMenuItemDropdown: React.FC<IProps> = ({
  menuItemsData,
  itemOverlayClassName,
}) => {
  const items = getMenuItems(menuItemsData);

  return (
    <React.Fragment>
      <GlobalStyle overlayClassName={itemOverlayClassName} />
      <SelectValidator className="select_validator">
        <MyDropdown
          placement={elementPlacement}
          menu={{ items: items.slice(1) }}
          overlayClassName={itemOverlayClassName}
        >
          <Space className="my_space_item">
            <MenuListItem menuItem={menuItemsData[0]} />
            <DownOutlined />
          </Space>
        </MyDropdown>
      </SelectValidator>
    </React.Fragment>
  );
};

const UseCasesMenuItemDropdownComponent = React.memo(UseCasesMenuItemDropdown);

export default UseCasesMenuItemDropdownComponent;
